import {ControlledInput, Modal, ModalContent, ModalTitle, spawnErrorToast} from '@hm/fundamental'
import {useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import type {FeatureCatalogItem} from '../../../api/types/featuresMenu'
import {useFeatures} from '../../../app/features-menu/useFeatures'

import {FeatureStoreContent} from './FeatureStoreContent'

interface FeatureStoreDialogProps {
  open: boolean
  addedFeatureIds: Set<number>
  onAddFeature: (feature: FeatureCatalogItem) => void
  onClose: () => void
}

export const FeatureStoreDialog: React.FC<FeatureStoreDialogProps> = ({
  open,
  addedFeatureIds,
  onAddFeature,
  onClose
}) => {
  const {t} = useTranslation()
  const {data: features = [], isError, isLoading} = useFeatures()
  const [filter, setFilter] = useState('')

  useEffect(() => {
    if (open && isError) {
      spawnErrorToast(t('suite.error.loadFeatures'))
    }
  }, [isError, open, t])
  const filteredFeatures = useMemo(() => {
    const searchTerm = filter.trim().toLowerCase()

    if (!searchTerm) {
      return features
    }

    return features.filter((feature) =>
      t(`suite.featureName.${feature.name}`, {defaultValue: feature.name})
        .toLowerCase()
        .includes(searchTerm)
    )
  }, [features, filter, t])

  return (
    <Modal open={open} onClose={onClose} data-cy="feature_store_modal">
      <ModalTitle>
        <div className="flex items-center gap-4 justify-between w-full">
          <div className="text-left">{t('suite.featureStore.title')}</div>
          <ControlledInput
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder={t('suite.featureStore.searchPlaceholder')}
            name="feature_store_search"
            className="w-full max-w-[16rem]"
            data-cy="feature_store_search"
          />
        </div>
      </ModalTitle>
      <ModalContent
        className="bg-container_background h-[60vh] overflow-y-auto"
        data-cy="feature_store_content"
      >
        <FeatureStoreContent
          isLoading={isLoading}
          features={filteredFeatures}
          addedFeatureIds={addedFeatureIds}
          onAddFeature={onAddFeature}
        />
      </ModalContent>
    </Modal>
  )
}
